import { useEffect, useRef } from 'react';
import { config } from '@fullcalendar/core/internal';

const DEFAULT_SCROLLGRID_RESIZE_INTERVAL = 500;

/**
 * Re-measures a FullCalendar instance once its layout has settled.
 * FullCalendar's ScrollGrid silently drops sizing requests beyond 10 per 500ms.
 * On a busy mount (many calendars, React labels rendered asynchronously) the sizing
 * that runs after the labels are painted can be dropped, leaving rows frozen at the
 * height of an empty cell. Nothing re-measures afterwards, so rows stay squashed.
 * A trailing updateSize after the throttle window fixes it, like FullCalendar's own
 * ensureScrollGridResize workaround. Also re-measures when web fonts finish loading
 * and when the wrapper width changes (height is not observed: it would loop with updateSize).
 * @param {React.RefObject<any>} calendarRef - Ref to the @fullcalendar/react component
 * @param {React.RefObject<HTMLElement | null>} wrapperRef - Ref to the element wrapping the calendar
 * @returns {void}
 */
function useCalendarAutoResize(calendarRef, wrapperRef) {
  const scheduleRef = useRef(/** @type {(() => void) | null} */ (null));

  useEffect(() => {
    let isActive = true;
    /** @type {ReturnType<typeof setTimeout> | null} */
    let timeoutId = null;
    const delay = (config.SCROLLGRID_RESIZE_INTERVAL ?? DEFAULT_SCROLLGRID_RESIZE_INTERVAL) + 1;

    const scheduleUpdateSize = () => {
      if (!isActive) return;
      if (timeoutId != null) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        calendarRef.current?.getApi()?.updateSize();
      }, delay);
    };
    scheduleRef.current = scheduleUpdateSize;

    const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
    fonts?.ready.then(scheduleUpdateSize);
    fonts?.addEventListener('loadingdone', scheduleUpdateSize);

    /** @type {ResizeObserver | null} */
    let resizeObserver = null;
    const wrapper = wrapperRef.current;
    if (wrapper && typeof ResizeObserver !== 'undefined') {
      let lastWidth = Math.round(wrapper.getBoundingClientRect().width);
      resizeObserver = new ResizeObserver(([entry]) => {
        const width = Math.round(entry.contentRect.width);
        if (width === lastWidth) return;
        lastWidth = width;
        scheduleUpdateSize();
      });
      resizeObserver.observe(wrapper);
    }

    return () => {
      isActive = false;
      scheduleRef.current = null;
      fonts?.removeEventListener('loadingdone', scheduleUpdateSize);
      resizeObserver?.disconnect();
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [calendarRef, wrapperRef]);

  // Every commit may trigger FullCalendar sizing that gets throttled: re-measure once it settles.
  useEffect(() => {
    scheduleRef.current?.();
  });
}

export default useCalendarAutoResize;
